import { useMemo } from 'react';
import { Steps, Typography, Tag } from 'antd';
import { GUIDED_STEPS, getNature } from '../productStudioModes';
import SmartDefaultsBanner from './SmartDefaultsBanner';
import BaseStockSelectorCard from './BaseStockSelectorCard';
import RelevantTechnicalMiniForm from './RelevantTechnicalMiniForm';
import BasicIdentityStep from './steps/BasicIdentityStep';
import ClassificationUsageStep from './steps/ClassificationUsageStep';
import GstPricingStep from './steps/GstPricingStep';
import ReviewSaveStep from './steps/ReviewSaveStep';

const { Title, Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Guided Create — a leaner 5-step take on the existing 7-step
// stepper. Reuses the same step components (BasicIdentityStep,
// GstPricingStep, ReviewSaveStep). Renders one nature-specific mini
// form in Step 4 instead of the full TechnicalDetailsStep with its
// four toggleable section cards.
//
// The parent's <Form> instance + save orchestration are shared, so
// nothing about the backend save path changes.
// ─────────────────────────────────────────────────────────────────

function NatureStep({ form, natureKey, smartDefaults, onChangeNature, currentItemId }) {
  const nature = getNature(natureKey);
  return (
    <div>
      <Title level={4} style={{ marginTop: 0 }}>What are you creating?</Title>
      <Text style={{ color: '#64748B' }}>
        You picked{' '}
        <Tag color="blue" style={{ fontSize: 12 }}>{nature ? nature.label : '—'}</Tag>
        {' '}from the entry screen. You can change it from the banner below.
      </Text>
      <div style={{ height: 12 }} />
      <SmartDefaultsBanner
        natureKey={natureKey}
        defaults={smartDefaults}
        onChangeNature={onChangeNature}
      />
      <div style={{ height: 4 }} />
      <BaseStockSelectorCard form={form} currentItemId={currentItemId} />
    </div>
  );
}

function ItemSpecificStep({ natureKey, disabled }) {
  const nature = getNature(natureKey);
  return (
    <div>
      <Title level={4} style={{ marginTop: 0 }}>Item-specific details</Title>
      <Text style={{ color: '#64748B' }}>
        Only the most-used technical fields for{' '}
        {nature ? <strong>{nature.label}</strong> : 'this item'}.
        {' '}Open the item in Advanced Create later for the full spec sheet.
      </Text>
      <div style={{ height: 12 }} />
      {nature && nature.extension ? (
        <RelevantTechnicalMiniForm natureKey={natureKey} disabled={disabled} />
      ) : (
        <div className="erp-studio-empty-hint" style={{ marginTop: 8 }}>
          No technical spec section needed for <strong>{nature ? nature.label : 'this item'}</strong>.
          You can skip ahead to Review.
        </div>
      )}
    </div>
  );
}

export default function ProductGuidedCreateFlow({
  form,
  currentIndex,
  setCurrentIndex,
  natureKey,
  smartDefaults,
  onChangeNature,
  enabledSections,
  imageCount,
  itemId,
  mode,
  watchedValues,
  saving
}) {
  const step = GUIDED_STEPS[currentIndex];

  // Stepper items — short titles, one-line subtitles.
  const stepperItems = useMemo(() =>
    GUIDED_STEPS.map((s, idx) => ({
      title: (
        <button
          type="button"
          className="erp-step-jump"
          onClick={() => setCurrentIndex(idx)}
          aria-current={idx === currentIndex ? 'step' : undefined}
        >
          {s.title}
        </button>
      ),
      description: <span>{s.subtitle}</span>,
      status: idx === currentIndex ? 'process' : (idx < currentIndex ? 'finish' : 'wait')
    })),
    [currentIndex, setCurrentIndex]
  );

  return (
    <div className="erp-studio-body">
      <div className="erp-studio-stepper" aria-label="Guided create steps">
        <Steps direction="vertical" current={currentIndex} size="small" items={stepperItems} />
      </div>

      <div className="erp-studio-content">
        {step.key === 'nature' ? (
          <NatureStep
            form={form}
            natureKey={natureKey}
            smartDefaults={smartDefaults}
            onChangeNature={onChangeNature}
            currentItemId={itemId}
          />
        ) : null}
        {step.key === 'basic' ? (
          <>
            <BasicIdentityStep disabled={saving} />
            <ClassificationUsageStep disabled={saving} currentItemId={itemId} />
          </>
        ) : null}
        {step.key === 'gst_pricing' ? (
          <GstPricingStep disabled={saving} />
        ) : null}
        {step.key === 'technical' ? (
          <ItemSpecificStep natureKey={natureKey} disabled={saving} />
        ) : null}
        {step.key === 'review' ? (
          <ReviewSaveStep
            values={watchedValues}
            enabledSections={enabledSections}
            imageCount={imageCount}
            mode={mode}
            itemId={itemId}
          />
        ) : null}
      </div>
    </div>
  );
}

export { GUIDED_STEPS };
